import { Divider, Tooltip, Typography } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import Button from 'components/new-ui/base/button/Button';
import DotSeparator from 'components/new-ui/base/dot-separator/DotSeparator';
import Tag from 'components/new-ui/base/tag/Tag';
import { Ics2CustomsRequest, Ics2Referral } from 'store/declarations/eu/ics2/customs-requests/types';
import { getRequestIssueDate, getResponseMessageType } from 'store/declarations/eu/ics2/customs-requests/utils';
import {
    formatRequestIssueDate,
    getRequestTagStatus,
    getRequestTitle,
    hasReferralBeenAnswered,
} from './referralLabels';
import ReferralRow from './ReferralRow';
import { CardDetailsToggle, RequestCardBody, RequestCardHeader, RequestCardLayout, RequestCardTitle } from '../styles';

type Props = {
    request: Ics2CustomsRequest;
    isExpanded: boolean;
    selectedIds: string[];
    draftErrors: Record<string, string[]>;
    isSending: boolean;
    isSubmitting: boolean;
    onToggleExpanded: () => void;
    onToggleSelected: (referral: Ics2Referral) => void;
    onOpenReferral: (referral: Ics2Referral) => void;
    onSend: () => void;
};

const RequestCard = ({
    request,
    isExpanded,
    selectedIds,
    draftErrors,
    isSending,
    isSubmitting,
    onToggleExpanded,
    onToggleSelected,
    onOpenReferral,
    onSend,
}: Props) => {
    const issuedAt = formatRequestIssueDate(getRequestIssueDate(request));

    const isAnswered =
        request.referrals.length > 0 && request.referrals.every((entry) => hasReferralBeenAnswered(entry.status));

    const selected = request.referrals.filter((referral) => selectedIds.includes(referral.id));
    const incomplete = selected.filter((referral) => draftErrors[referral.id]?.length);

    const sendBlockedReason = !selected.length
        ? 'Select the referrals to answer first.'
        : incomplete.length
        ? `${incomplete.length} of ${selected.length} selected answer${
              selected.length === 1 ? ' is' : 's are'
          } incomplete.`
        : undefined;

    const sendButton = (
        <Button loading={isSending} disabled={Boolean(sendBlockedReason) || isSubmitting} onClick={onSend}>
            Send Response
        </Button>
    );

    return (
        <RequestCardLayout>
            <RequestCardHeader>
                <RequestCardTitle>
                    <Tag status={getRequestTagStatus(request.messageType)} label={request.messageType} />
                    <Typography.Text strong>{getRequestTitle(request.messageType)}</Typography.Text>
                    {issuedAt && (
                        <>
                            <DotSeparator />
                            <Typography.Text type="secondary">{issuedAt}</Typography.Text>
                        </>
                    )}
                </RequestCardTitle>

                {isAnswered ? (
                    <Tag status="success" label="Response Sent to Customs" />
                ) : (
                    <Tooltip
                        title={
                            sendBlockedReason ??
                            `Sends the selected referrals as a single ${getResponseMessageType(request)}.`
                        }
                    >
                        <span>{sendButton}</span>
                    </Tooltip>
                )}
            </RequestCardHeader>

            {isExpanded && (
                <RequestCardBody>
                    {request.referrals.map((referral, index) => (
                        <div key={referral.id}>
                            {index > 0 && <Divider style={{ margin: 0 }} />}
                            <ReferralRow
                                referral={referral}
                                isSelected={selectedIds.includes(referral.id)}
                                draftErrors={draftErrors[referral.id]}
                                onToggleSelected={() => onToggleSelected(referral)}
                                onOpen={() => onOpenReferral(referral)}
                            />
                        </div>
                    ))}
                </RequestCardBody>
            )}

            <CardDetailsToggle type="button" aria-expanded={isExpanded} onClick={onToggleExpanded}>
                {isExpanded ? <UpOutlined /> : <DownOutlined />}
                {isExpanded ? 'Hide Details' : 'Show Details'}
            </CardDetailsToggle>
        </RequestCardLayout>
    );
};

export default RequestCard;
